import { useState } from "react";
import { ChevronDown, ChevronRight, FolderPlus, MoreVertical, NotebookPen, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { NoteFolder, NoteFolderNode } from "@/lib/composerNotes";

export type NoteSelection =
  | { kind: "root" }
  | { kind: "folder"; folderId: string };

type Props = {
  tree: NoteFolderNode[];
  rootNoteCount: number;
  selection: NoteSelection;
  onSelect: (s: NoteSelection) => void;
  onCreateFolder: (parent: NoteFolder | null) => void;
  onRenameFolder: (folder: NoteFolder) => void;
  onDeleteFolder: (folder: NoteFolder) => void;
  onMoveNote: (noteId: string, folderId: string | null) => void;
  onMoveFolder: (folderId: string, newParentId: string | null) => void;
};

const NOTE_MIME = "application/x-excerptus-note";
const FOLDER_MIME = "application/x-excerptus-note-folder";

export const NotesFolderTreeArchive = ({
  tree,
  rootNoteCount,
  selection,
  onSelect,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onMoveNote,
  onMoveFolder,
}: Props) => {
  const [rootHover, setRootHover] = useState(false);
  const isRootSelected = selection.kind === "root";

  return (
    <ul className="space-y-0.5">
      {/* Permanent "Notes" row — top-level peer of user folders */}
      <li>
        <div
          onDragOver={(e) => {
            if (
              !e.dataTransfer.types.includes(NOTE_MIME) &&
              !e.dataTransfer.types.includes(FOLDER_MIME)
            ) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            setRootHover(true);
          }}
          onDragLeave={() => setRootHover(false)}
          onDrop={(e) => {
            setRootHover(false);
            const notePayload = e.dataTransfer.getData(NOTE_MIME);
            const folderPayload = e.dataTransfer.getData(FOLDER_MIME);
            if (notePayload) {
              e.preventDefault();
              try {
                const { id } = JSON.parse(notePayload) as { id: string };
                if (id) onMoveNote(id, null);
              } catch { /* noop */ }
            } else if (folderPayload) {
              e.preventDefault();
              try {
                const { id } = JSON.parse(folderPayload) as { id: string };
                if (id) onMoveFolder(id, null);
              } catch { /* noop */ }
            }
          }}
          className={cn(
            "group flex items-center gap-1 rounded-md px-1.5 py-1 text-sm",
            isRootSelected ? "bg-accent text-accent-foreground" : "hover:bg-muted/60",
            rootHover && "ring-1 ring-primary/60 bg-primary/5",
          )}
        >
          <span className="block h-5 w-5 shrink-0" />
          <NotebookPen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <button
            type="button"
            className="flex-1 truncate text-left font-medium"
            onClick={() => onSelect({ kind: "root" })}
            title="Notes"
          >
            Notes
            {rootNoteCount > 0 && (
              <span className="ml-1.5 text-[10px] text-muted-foreground">{rootNoteCount}</span>
            )}
          </button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 opacity-0 group-hover:opacity-100"
            aria-label="New folder"
            onClick={() => onCreateFolder(null)}
          >
            <FolderPlus className="h-3.5 w-3.5" />
          </Button>
        </div>
      </li>

      {tree.map((node) => (
        <FolderRow
          key={node.id}
          node={node}
          depth={0}
          selection={selection}
          onSelect={onSelect}
          onCreateFolder={onCreateFolder}
          onRenameFolder={onRenameFolder}
          onDeleteFolder={onDeleteFolder}
          onMoveNote={onMoveNote}
          onMoveFolder={onMoveFolder}
        />
      ))}
    </ul>
  );
};

type RowProps = {
  node: NoteFolderNode;
  depth: number;
  selection: NoteSelection;
  onSelect: (s: NoteSelection) => void;
  onCreateFolder: (parent: NoteFolder | null) => void;
  onRenameFolder: (folder: NoteFolder) => void;
  onDeleteFolder: (folder: NoteFolder) => void;
  onMoveNote: (noteId: string, folderId: string | null) => void;
  onMoveFolder: (folderId: string, newParentId: string | null) => void;
};

const FolderRow = ({
  node,
  depth,
  selection,
  onSelect,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onMoveNote,
  onMoveFolder,
}: RowProps) => {
  const [open, setOpen] = useState(true);
  const [hover, setHover] = useState(false);
  const isSelected = selection.kind === "folder" && selection.folderId === node.id;
  const hasChildren = node.children.length > 0;

  return (
    <li>
      <div
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData(FOLDER_MIME, JSON.stringify({ id: node.id }));
          e.dataTransfer.effectAllowed = "move";
        }}
        onDragOver={(e) => {
          if (
            !e.dataTransfer.types.includes(NOTE_MIME) &&
            !e.dataTransfer.types.includes(FOLDER_MIME)
          ) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          setHover(true);
        }}
        onDragLeave={() => setHover(false)}
        onDrop={(e) => {
          setHover(false);
          const notePayload = e.dataTransfer.getData(NOTE_MIME);
          const folderPayload = e.dataTransfer.getData(FOLDER_MIME);
          if (notePayload) {
            e.preventDefault();
            e.stopPropagation();
            try {
              const { id } = JSON.parse(notePayload) as { id: string };
              if (id) onMoveNote(id, node.id);
            } catch { /* noop */ }
            return;
          }
          if (folderPayload) {
            e.preventDefault();
            e.stopPropagation();
            try {
              const { id } = JSON.parse(folderPayload) as { id: string };
              if (id && id !== node.id) {
                onMoveFolder(id, node.id);
                setOpen(true);
              }
            } catch { /* noop */ }
          }
        }}
        className={cn(
          "group relative flex items-center gap-1 rounded-md px-1.5 py-1 text-sm",
          isSelected ? "bg-accent text-accent-foreground" : "hover:bg-muted/60",
          hover && "ring-1 ring-primary/60 bg-primary/5",
        )}
        style={{ paddingLeft: `${depth * 12 + 4}px` }}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
          aria-label={open ? "Collapse" : "Expand"}
        >
          {hasChildren ? (
            open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />
          ) : (
            <span className="block h-3.5 w-3.5" />
          )}
        </button>
        <button
          type="button"
          onClick={() => onSelect({ kind: "folder", folderId: node.id })}
          className="flex-1 truncate text-left"
          title={node.name}
        >
          {node.name}
          {node.noteCount > 0 && (
            <span className="ml-1.5 text-[10px] text-muted-foreground">{node.noteCount}</span>
          )}
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
              aria-label="Folder actions"
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onSelect={() => onCreateFolder(node)}>
              <FolderPlus className="mr-2 h-4 w-4" /> New sub-folder
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onRenameFolder(node)}>
              <Pencil className="mr-2 h-4 w-4" /> Rename
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => onDeleteFolder(node)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {open && hasChildren && (
        <ul className="space-y-0.5">
          {node.children.map((child) => (
            <FolderRow
              key={child.id}
              node={child}
              depth={depth + 1}
              selection={selection}
              onSelect={onSelect}
              onCreateFolder={onCreateFolder}
              onRenameFolder={onRenameFolder}
              onDeleteFolder={onDeleteFolder}
              onMoveNote={onMoveNote}
              onMoveFolder={onMoveFolder}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

